// Owner-side data provider. Loads the registry + selected business, and exposes helpers that mutate
// the whole business document and persist it via save-business.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { BusinessData, Registry } from "@shared/types.ts";
import { api } from "../lib/api.ts";

interface Ctx {
  registry: Registry;
  data: BusinessData | null;
  businessId: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  switchBusiness: (id: string) => void;
  reload: () => Promise<void>;
  save: (next: BusinessData) => Promise<void>;
  mutate: (fn: (draft: BusinessData) => void) => Promise<void>;
  seedLittleRick: () => Promise<void>;
}

const DataCtx = createContext<Ctx | null>(null);
const LS_KEY = "lrp_business";

export function DataProvider({ children }: { children: ReactNode }) {
  const [registry, setRegistry] = useState<Registry>({ businesses: [] });
  const [data, setData] = useState<BusinessData | null>(null);
  const [businessId, setBusinessId] = useState<string>(() => localStorage.getItem(LS_KEY) || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.state(id);
      setRegistry(res.registry);
      setData(res.data);
      if (res.data) {
        setBusinessId(res.data.business.id);
        localStorage.setItem(LS_KEY, res.data.business.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(businessId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchBusiness = useCallback((id: string) => {
    setBusinessId(id);
    localStorage.setItem(LS_KEY, id);
    void load(id);
  }, [load]);

  const reload = useCallback(async () => { await load(businessId || undefined); }, [load, businessId]);

  const save = useCallback(async (next: BusinessData) => {
    setSaving(true);
    setError(null);
    setData(next); // optimistic
    try {
      await api.saveBusiness(next);
      // refresh registry name if it changed
      setRegistry((r) => {
        const exists = r.businesses.find((b) => b.id === next.business.id);
        if (!exists) return { businesses: [...r.businesses, { id: next.business.id, name: next.business.name }] };
        return { businesses: r.businesses.map((b) => (b.id === next.business.id ? { ...b, name: next.business.name } : b)) };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
      throw e;
    } finally {
      setSaving(false);
    }
  }, []);

  const mutate = useCallback(async (fn: (draft: BusinessData) => void) => {
    if (!data) return;
    const draft = structuredClone(data) as BusinessData;
    fn(draft);
    await save(draft);
  }, [data, save]);

  const seedLittleRick = useCallback(async () => {
    setSaving(true);
    try {
      const res = await api.seed();
      await load(res.businessId);
    } finally {
      setSaving(false);
    }
  }, [load]);

  const value: Ctx = { registry, data, businessId, loading, saving, error, switchBusiness, reload, save, mutate, seedLittleRick };
  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

export function useData(): Ctx {
  const c = useContext(DataCtx);
  if (!c) throw new Error("useData outside provider");
  return c;
}
